import type { ZodSafeParseResult, ZodSchema } from 'zod'
import type { IBindFieldContext, IBindFieldModel } from '.'
import type {
	BasicOpenValueType,
	FOperator,
	ICellValue,
	IField,
	InternalState,
	IOpenFilterValue,
} from '../types'

import { BasicValueType, FOperatorDescMap } from '../types'

export abstract class Field {
	// eslint-disable-next-line sonarjs/public-static-readonly
	static bindContext: IBindFieldContext
	// eslint-disable-next-line sonarjs/public-static-readonly
	static bindModel: IBindFieldModel

	constructor(
		public field: IField,
		public state: InternalState
	) {}

	/**
	 * Each field has its own openValue for use in the widget SDK, robot variables and subsequent open scenarios.
	 * This is open to developers and will do some processing on cellValue.
	 * This method describes the structure of the field openValue. Currently used in robot variables.
	 */
	abstract get openValueSchema(): ZodSchema

	/**
	 * cellValue is converted to the value used in the external call
	 * (the event will contain the original value and the converted value, mainly exposed to external developers, automation, widget-sdk)
	 * @param cellValue
	 */
	abstract cellValueToOpenValue(
		cellValue: ICellValue
	): BasicOpenValueType | null

	/**
	 * openWriteValue is converted to cellValue (mainly exposed to external developers, automation, widget-sdk write data)
	 * @param openWriteValue
	 */
	abstract openWriteValueToCellValue(openWriteValue: any): ICellValue

	// not includes basic value type in Array
	get valueType(): Omit<BasicValueType, BasicValueType.Array> {
		if (this.basicValueType === BasicValueType.Array) {
			return this.innerBasicValueType
		}

		return this.basicValueType
	}

	// default String, Array type field override
	get innerBasicValueType(): BasicValueType {
		return BasicValueType.String
	}

	showFOperatorDesc(type: FOperator) {
		return FOperatorDescMap[type]
	}
	coerceValue(value: any): any | null {
		return value
	}
	/**
	 * whether the field support grouping
	 */
	get canGroup(): boolean {
		return true
	}

	/**
	 * whether the calc field error
	 */
	get hasError(): boolean {
		return false
	}

	/**
	 * whether the field can be filter
	 */
	get canFilter(): boolean {
		return true
	}

	/**
	 * calc field exception info
	 */
	get warnText(): string {
		return ''
	}

	/**
	 * current field cell value's basic types.
	 */
	abstract get basicValueType(): BasicValueType

	/**
	 * current operator types that fieldType support, and the order in the dropdown box
	 * every heritage field must be implemented
	 */
	abstract get acceptFilterOperators(): FOperator[]

	/**
	 * The value of each cell is calculated through the field record relationship in the snapshot.
	 * But the calculation here refers to fields such as LookUp, RollUp, Formula, etc.
	 * The calculation of the cell value depends on the value of other basic cells.
	 * Such fields are called "calculated fields" and need to override this method.
	 * @readonly
	 * @memberof Field
	 */
	get isComputed() {
		return false
	}

	/**
	 * Whether the current field can be edited
	 * Not all fields allow users to edit. If it is a calculated field, such as rollUp, lookup, formula fields, users are not allowed to edit
	 * In addition, related fields are only allowed to be edited
	 * if the user has the editing permission of the table associated with the current related field.
	 */
	recordEditable(): boolean {
		return false
	}

	/**
	 * Whether the properties of the current field are allowed to be edited
	 * Here it is judged according to the permission factor, and the edit field is allowed to be true.
	 */
	propertyEditable(): boolean {
		return false
	}

	abstract cellValueToString(cellValue: ICellValue): string | null

	/**
	 * Convert to standard output of the api
	 * @param cellValue
	 */
	abstract cellValueToApiStandardValue(cellValue: ICellValue): any

	/**
	 * Convert to the output the user sees in the table
	 * @param cellValue
	 */
	abstract cellValueToApiStringValue(cellValue: ICellValue): string | null

	abstract validate(value: any): boolean

	/**
	 * Each column type corresponds to a data format.
	 * By checking the data format, it is determined whether the data written to the cell is as expected
	 * @returns {ZodSafeParseResult<ICellValue>}
	 */
	abstract validateCellValue(
		cellValue: ICellValue
	): ZodSafeParseResult<ICellValue>

	/**
	 * Each column type corresponds to a data format.
	 * By checking the data format, it is determined whether the data written to the cell is as expected
	 * @returns {ZodSafeParseResult<ICellValue>}
	 */
	abstract validateOpenWriteValue(openWriteValue: any): ZodSafeParseResult<any>

	/**
	 * Whether to multi-select fields of value type.
	 * For multiple selections, the collaborator field will return true.
	 * NOTE: Fields that are guaranteed to return true, value is an array.
	 */
	isMultiValueField(): boolean {
		return false
	}

	/**
	 * Returns the default value of the field attribute configuration when adding a record
	 */
	defaultValue(): ICellValue {
		return null
	}

	/**
	 * Converting internal filter structures to external data structures.
	 * @param _value
	 */
	filterValueToOpenFilterValue(_value: any): IOpenFilterValue {
		throw new Error(` not support filterValueToOpenFilterValue`)
	}

	/**
	 * Converting external filter structures to internal data structures.
	 * @param value
	 */
	openFilterValueToFilterValue(_value: IOpenFilterValue): any {
		throw new Error(` not support filterValueToOpenFilterValue`)
	}

	/**
	 * Convert the obtained color name to color number
	 * @param name color name
	 */
	getOptionColorNumberByName(name: string) {
		return undefined
	}
}
